import type { GroupWithMemberCount, UserRole } from '../types/database';
import type { EventAudienceSectionMode } from '../types/events.types';
import { getOrderedRoleKeys, getRoleLabelByKey } from './roles.utils';

/**
 * The role keys, in display order, that the event-audience UI iterates. Sourced
 * from the admin-managed role registry so new roles are targetable automatically.
 */
export function getEventAudienceRoleOrder(): string[] {
  return getOrderedRoleKeys();
}

export function getEventAudienceRoleLabel(role: UserRole): string {
  return getRoleLabelByKey(role);
}

export function getEventAudienceSectionMode(
  role: UserRole,
  roleTargets: Set<UserRole>,
  groupTargetIds: Set<string>,
  groups: GroupWithMemberCount[]
): EventAudienceSectionMode {
  if (roleTargets.has(role)) {
    return 'all';
  }

  return groups.some(
    (group) => group.parent_role === role && groupTargetIds.has(group.id)
  )
    ? 'groups'
    : 'none';
}

export function getSelectedAudienceGroupIds(
  role: UserRole,
  groupTargetIds: Set<string>,
  groups: GroupWithMemberCount[]
): string[] {
  return groups
    .filter(
      (group) => group.parent_role === role && groupTargetIds.has(group.id)
    )
    .map((group) => group.id);
}

export function getEventAudienceGroupsForRole(
  role: UserRole,
  groups: GroupWithMemberCount[],
  selectedGroupIds = new Set<string>()
): GroupWithMemberCount[] {
  return groups.filter(
    (group) =>
      group.parent_role === role &&
      (!group.archived_at || selectedGroupIds.has(group.id))
  );
}

export function getSelectedEventAudienceGroupsForRole(
  role: UserRole,
  groups: GroupWithMemberCount[],
  selectedGroupIds: Set<string>
): GroupWithMemberCount[] {
  return getEventAudienceGroupsForRole(role, groups, selectedGroupIds).filter(
    (group) => selectedGroupIds.has(group.id)
  );
}

export function formatEventAudienceRoleSummary(
  role: UserRole,
  mode: EventAudienceSectionMode,
  groups: GroupWithMemberCount[],
  selectedGroupIds: Set<string>
): string {
  if (mode === 'all') {
    return `All current ${getEventAudienceRoleLabel(role)} users`;
  }

  const roleGroups = getEventAudienceGroupsForRole(
    role,
    groups,
    selectedGroupIds
  );

  if (mode === 'groups') {
    const selectedGroupCount = getSelectedEventAudienceGroupsForRole(
      role,
      groups,
      selectedGroupIds
    ).length;

    return `${selectedGroupCount} of ${roleGroups.length} groups`;
  }

  const groupLabel = roleGroups.length === 1 ? 'group' : 'groups';

  return `${roleGroups.length} ${groupLabel} available`;
}

export function getEventAudienceNarrowedLabel(
  role: UserRole,
  groups: GroupWithMemberCount[],
  selectedGroupIds: Set<string>
): string | null {
  const roleGroups = getEventAudienceGroupsForRole(
    role,
    groups,
    selectedGroupIds
  );
  const selectedGroups = roleGroups.filter((group) =>
    selectedGroupIds.has(group.id)
  );

  if (selectedGroups.length === 0) {
    return null;
  }

  const [firstGroup] = selectedGroups;
  const additionalGroupCount = selectedGroups.length - 1;
  const countLabel = `${selectedGroups.length} of ${roleGroups.length} groups`;

  return additionalGroupCount > 0
    ? `${firstGroup.display_name} +${additionalGroupCount} · ${countLabel}`
    : `${firstGroup.display_name} · ${countLabel}`;
}

export function getSelectedEventAudienceGroupMemberTotal(
  role: UserRole,
  groups: GroupWithMemberCount[],
  selectedGroupIds: Set<string>
): number {
  return getSelectedEventAudienceGroupsForRole(
    role,
    groups,
    selectedGroupIds
  ).reduce((total, group) => total + Number(group.member_count), 0);
}

export function hasExplicitEventAudienceTarget(
  roleTargets?: Set<UserRole>,
  groupTargetIds?: Set<string>
): boolean {
  return Boolean(
    (roleTargets && roleTargets.size > 0) ||
    (groupTargetIds && groupTargetIds.size > 0)
  );
}
